import React, { useEffect, useMemo, useState } from 'react';
import { getSites } from '../../../services/asset.service';
import { listOrganizations, listUsersAdmin } from '../../../services/user.service';
import {
  filterAssignmentSearchResults,
  inferAssignmentKind,
  normalizeAssignmentOption,
  sanitizeConnectorSearchInput,
} from './connectorFormUtils';

function optionLabel(option) {
  return `${option.site} · ${option.kind}`;
}

export default function ScannerAssignmentPicker({
  selectedIds = [],
  knownAssignments = [],
  perimeterScanner = false,
  readOnly = false,
  onChange,
}) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [pendingRemove, setPendingRemove] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');

    Promise.allSettled([getSites(), listOrganizations(), listUsersAdmin()])
      .then(([sitesResult, orgsResult, usersResult]) => {
        if (cancelled) return;
        const sites = sitesResult.status === 'fulfilled' && Array.isArray(sitesResult.value)
          ? sitesResult.value
          : [];
        const orgs = orgsResult.status === 'fulfilled' && Array.isArray(orgsResult.value)
          ? orgsResult.value
          : [];
        const users = usersResult.status === 'fulfilled' && Array.isArray(usersResult.value)
          ? usersResult.value
          : [];

        const emailsByOrgId = new Map();
        const addEmail = (orgId, email) => {
          const key = String(orgId || '').trim();
          const value = String(email || '').trim().toLowerCase();
          if (!key || !value) return;
          const current = emailsByOrgId.get(key) || [];
          if (!current.includes(value)) emailsByOrgId.set(key, [...current, value]);
        };

        orgs.forEach((org) => {
          const orgId = String(org?.uu_id || org?.id || org?.organization_id || '').trim();
          ['email', 'owner_email', 'contact_email', 'billing_email'].forEach((key) => addEmail(orgId, org?.[key]));
        });
        users.forEach((user) => {
          const email = String(user?.email || '').trim();
          const orgIds = [
            user?.organization_id,
            user?.organization,
            user?.org_id,
            ...(Array.isArray(user?.organization_access_uuids) ? user.organization_access_uuids : []),
          ];
          orgIds.forEach((orgId) => addEmail(orgId, email));
        });

        const nextCatalog = sites
          .map((site) => {
            const orgId = String(site?.organization_id || site?.organization_uuid || site?.org_id || '').trim();
            return normalizeAssignmentOption(site, {
              emails: emailsByOrgId.get(orgId) || [],
              kind: inferAssignmentKind(site, perimeterScanner),
            });
          })
          .filter((row) => row.id);

        setCatalog(nextCatalog);
        if (sitesResult.status === 'rejected') {
          setLoadError('Could not load sites for assignment search.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSet = useMemo(() => new Set(selectedIds.map((id) => String(id))), [selectedIds]);

  const selectedOptions = useMemo(() => {
    const byId = new Map();
    knownAssignments.forEach((option) => {
      if (option?.id) byId.set(String(option.id), option);
    });
    catalog.forEach((option) => {
      byId.set(String(option.id), {
        ...option,
        kind: inferAssignmentKind(option, perimeterScanner),
      });
    });
    return selectedIds
      .map((id) => byId.get(String(id)))
      .filter(Boolean)
      .map((option) => ({
        ...option,
        kind: option.kind || inferAssignmentKind(option, perimeterScanner),
      }));
  }, [catalog, knownAssignments, perimeterScanner, selectedIds]);

  const results = useMemo(
    () => filterAssignmentSearchResults(catalog, query, selectedIds),
    [catalog, query, selectedIds]
  );

  const addAssignment = (option) => {
    if (readOnly || !option?.id || selectedSet.has(String(option.id))) return;
    onChange([...selectedIds, option.id]);
    setQuery('');
  };

  const requestRemove = (option) => {
    if (readOnly) return;
    const wasKnown = knownAssignments.some((item) => String(item.id) === String(option.id));
    if (wasKnown) {
      setPendingRemove(option);
      return;
    }
    onChange(selectedIds.filter((id) => String(id) !== String(option.id)));
  };

  const confirmRemove = () => {
    if (!pendingRemove) return;
    onChange(selectedIds.filter((id) => String(id) !== String(pendingRemove.id)));
    setPendingRemove(null);
  };

  return (
    <div className="connector-assignment-picker" data-tour="settings-connectors-modal-assignment">
      {selectedOptions.length > 0 ? (
        <ul className="connector-assignment-chip-list">
          {selectedOptions.map((option) => (
            <li key={option.id}>
              <span className="connector-assignment-chip">
                <span className="connector-assignment-chip-label" title={`${option.org} / ${option.site}`}>
                  {optionLabel(option)}
                </span>
                {readOnly ? null : (
                  <button
                    type="button"
                    className="connector-assignment-chip-remove"
                    onClick={() => requestRemove(option)}
                    aria-label={`Remove assignment ${optionLabel(option)}`}
                  >
                    ×
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="connector-assignment-empty">No sites assigned yet.</p>
      )}

      {pendingRemove ? (
        <div className="connector-assignment-confirm" role="alertdialog" aria-labelledby="connector-assignment-remove-title">
          <p id="connector-assignment-remove-title">
            Remove <strong>{optionLabel(pendingRemove)}</strong> from this scanner?
          </p>
          <div className="connector-assignment-confirm-actions">
            <button type="button" className="ghost-exposure-sys-btn ghost-exposure-sys-btn--secondary" onClick={() => setPendingRemove(null)}>
              Keep
            </button>
            <button type="button" className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary" onClick={confirmRemove}>
              Remove
            </button>
          </div>
        </div>
      ) : null}

      {readOnly ? null : (
        <div className="connector-assignment-search">
          <label className="edi-sr-only" htmlFor="connector-assignment-search-input">
            Search organizations, sites, or email
          </label>
          <input
            id="connector-assignment-search-input"
            type="search"
            className="ghost-exposure-sys-input connector-openvas-assignment-search-input"
            value={query}
            onChange={(e) => setQuery(sanitizeConnectorSearchInput(e.target.value))}
            placeholder="Search organization, site, or email"
            autoComplete="off"
            disabled={loading}
          />
          {loading ? (
            <p className="connector-assignment-empty" role="status">Loading sites…</p>
          ) : null}
          {loadError ? (
            <p className="profile-settings-message profile-settings-error" role="alert">
              {loadError}
            </p>
          ) : null}
          {!loading && query.trim() && results.length === 0 ? (
            <p className="connector-assignment-empty">No organizations or sites match that search.</p>
          ) : null}
          {!loading && results.length > 0 ? (
            <ul className="connector-assignment-results" role="listbox" aria-label="Assignment search results">
              {results.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    className="connector-assignment-result"
                    onClick={() => addAssignment(option)}
                  >
                    <span className="connector-assignment-result-title">{option.site}</span>
                    <span className="connector-assignment-result-meta">
                      {option.org}
                      {option.emails[0] ? ` · ${option.emails[0]}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}
